import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { VariaveisDisponiveisDialogComponent, VariaveisDisponiveisDialogData, VariaveisCursorPayload } from '@shared/components/formula/variaveis-disponiveis-dialog/variaveis-disponiveis-dialog.component';
import { listarVariaveisObservacao } from '@shared/components/formula/utils/formula-contexto.util';
import { planejarTrocaCodigoFormulas } from '@shared/components/formula/utils/troca-codigo-formulas.util';
import { CClassTribIbsCbsStruct } from '@shared/services/structs/motor-fiscal/c-class-trib-ibs-cbs.struct';
import { CstIbsCbsCclasstribIbsCbsStruct } from '@shared/services/structs/motor-fiscal/cst-ibs-cbs-cclasstrib-ibs-cbs.struct';
import { LookupItemStruct } from '@shared/services/structs/motor-fiscal/lookup-item.struct';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { ModeloDocumento } from '@shared/services/models/dfe/modelo-documento.model';
import { RegraTributariaModelosDocumentoSectionComponent } from './sections/modelos-documento-section/regra-tributaria-modelos-documento-section.component';
import { RegraTributariaClassificacaoTributariaSectionComponent } from './sections/classificacao-tributaria-section/regra-tributaria-classificacao-tributaria-section.component';
import { RegraTributariaObservacaoSectionComponent } from './sections/observacao-section/regra-tributaria-observacao-section.component';
import { RegraTributariaInformacaoFiscoSectionComponent } from './sections/informacao-fisco-section/regra-tributaria-informacao-fisco-section.component';

/** Variável do redutor da base de cálculo do IBS/CBS (percentualReducaoBaseCalculo da regra). */
const VARIAVEL_REDUCAO_BASE_CALCULO_IBS_CBS = 'ibscbs.p_reducao_base_calculo';

/** Aba Dados Gerais: identificação da regra, classificação tributária, modelos de documento e textos da nota. */
@Component({
    selector: 'app-regra-tributaria-dados-gerais-tab',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSlideToggleModule,
        MatIconModule,
        RegraTributariaModelosDocumentoSectionComponent,
        RegraTributariaClassificacaoTributariaSectionComponent,
        RegraTributariaObservacaoSectionComponent,
        RegraTributariaInformacaoFiscoSectionComponent,
    ],
    templateUrl: './regra-tributaria-dados-gerais-tab.component.html',
    styleUrl: './regra-tributaria-dados-gerais-tab.component.css',
})
export class RegraTributariaDadosGeraisTabComponent implements OnInit {
    @Input({ required: true }) form!: FormGroup;
    @Input() modelos: ModeloDocumento[] = [];
    @Input() csts: CClassTribIbsCbsStruct[] = [];
    @Input() cclasstribs: CClassTribIbsCbsStruct[] = [];
    @Input() relacoes: CstIbsCbsCclasstribIbsCbsStruct[] = [];
    @Input() isModoPadrao = false;
    @Input() indicadoresDoacao: LookupItemStruct[] = [];
    /** Todas as fórmulas do catálogo `regra-tributaria`, com a chave do contexto de origem. */
    @Input() formulas: FormulaCatalogoItemStruct[] = [];

    /** Variáveis oferecidas nas observações: fórmulas dos grupos que a regra calcula. */
    public variaveisObservacao: VariavelStruct[] = [];

    private readonly destroyRef = inject(DestroyRef);
    private classificacaoAnterior: { cstIbsCbs: string | null; cclassTrib: string | null } = { cstIbsCbs: null, cclassTrib: null };
    private classificacaoTratada: { cstIbsCbs: string | null; cclassTrib: string | null } = { cstIbsCbs: null, cclassTrib: null };
    private restaurandoClassificacao = false;

    constructor(private dialog: MatDialog) {}

    public ngOnInit(): void {
        this.classificacaoAnterior = this.lerClassificacao();
        this.classificacaoTratada = this.classificacaoAnterior;
        this.atualizarVariaveisObservacao();

        // Toggles dos grupos mudam o que a regra calcula: a lista acompanha o formulário inteiro.
        this.form.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.atualizarVariaveisObservacao());
        // Inscrito depois do cadastro: roda com os toggles já recalculados pelos indicadores do novo CST/cClassTrib.
        this.classificacaoForm.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.confirmarTrocaClassificacao());
    }

    public get classificacaoForm(): FormGroup {
        return this.form.get('classificacao') as FormGroup;
    }

    public get isClassificacaoInformada(): boolean {
        const classificacao = this.lerClassificacao();
        return !!classificacao.cstIbsCbs && !!classificacao.cclassTrib;
    }

    private lerClassificacao(): { cstIbsCbs: string | null; cclassTrib: string | null } {
        return {
            cstIbsCbs: this.classificacaoForm?.get('cstIbsCbs')?.value ?? null,
            cclassTrib: this.classificacaoForm?.get('cclassTrib')?.value ?? null,
        };
    }

    /**
     * Fórmulas cujas variáveis podem ir para as observações. Mesmo mapa grupo ligado -> contexto do Motor Fiscal:
     * somente os contextos dos grupos ligados na regra, sem critério por CST ou cClassTrib.
     * As variáveis saem de `listarVariaveisObservacao` (saída e permitidas), como nas abas da natureza.
     */
    private formulasObservacao(): FormulaCatalogoItemStruct[] {
        const v = this.form.getRawValue();
        const cst = v.classificacao?.cstIbsCbs ?? null;
        const cclassTrib = v.classificacao?.cclassTrib ?? null;
        if (!cst || !cclassTrib) return [];

        const ibsCbs = !!v.ibsCbs?.isAtivo;
        const cbs = ibsCbs && !!v.ibsCbs?.cbs;
        const ibsUf = ibsCbs && !!v.ibsCbs?.ibsUf;
        const ibsMunicipio = ibsCbs && !!v.ibsCbs?.ibsMunicipio;
        const mono = !!v.mono?.isAtivo;
        const monoPadrao = mono && !!v.mono?.padrao?.isAtivo;
        const monoRetencao = mono && !!v.mono?.retencao?.isAtivo;
        const monoRetidaAnteriormente = mono && !!v.mono?.retidaAnteriormente?.isAtivo;
        const monoDiferimento = mono && !!v.mono?.diferimento?.isAtivo;
        const creditoPresumido = !!v.creditoPresumido?.isAtivo;
        const contextosCalculados: Record<string, boolean> = {
            'regra-tributaria.ibs-cbs.calculo': ibsCbs,
            'regra-tributaria.ibs-cbs.calculo.cbs': cbs,
            // A redução roda sempre com o ente ligado: ela calcula a alíquota efetiva (sem redução, a alíquota cheia).
            'regra-tributaria.ibs-cbs.calculo.cbs.reducao': cbs,
            'regra-tributaria.ibs-cbs.calculo.cbs.diferimento': cbs && !!v.ibsCbs?.cbs?.diferimentoAtivo,
            'regra-tributaria.ibs-cbs.calculo.cbs.devolvido': cbs && !!v.ibsCbs?.cbs?.devolucaoAtivo,
            'regra-tributaria.ibs-cbs.calculo.ibs-uf': ibsUf,
            'regra-tributaria.ibs-cbs.calculo.ibs-uf.reducao': ibsUf,
            'regra-tributaria.ibs-cbs.calculo.ibs-uf.diferimento': ibsUf && !!v.ibsCbs?.ibsUf?.diferimentoAtivo,
            'regra-tributaria.ibs-cbs.calculo.ibs-uf.devolvido': ibsUf && !!v.ibsCbs?.ibsUf?.devolucaoAtivo,
            'regra-tributaria.ibs-cbs.calculo.ibs-municipio': ibsMunicipio,
            'regra-tributaria.ibs-cbs.calculo.ibs-municipio.reducao': ibsMunicipio,
            'regra-tributaria.ibs-cbs.calculo.ibs-municipio.diferimento': ibsMunicipio && !!v.ibsCbs?.ibsMunicipio?.diferimentoAtivo,
            'regra-tributaria.ibs-cbs.calculo.ibs-municipio.devolvido': ibsMunicipio && !!v.ibsCbs?.ibsMunicipio?.devolucaoAtivo,
            'regra-tributaria.tributacao-regular.calculo.regular': ibsCbs && !!v.tributacaoRegular?.isAtivo,
            // Total monofásico do item: roda com qualquer modalidade do monofásico ligada.
            'regra-tributaria.tributacao-monofasica.calculo.ad-valorem': monoPadrao || monoRetencao || monoRetidaAnteriormente || monoDiferimento,
            'regra-tributaria.tributacao-monofasica.calculo.padrao': monoPadrao,
            'regra-tributaria.tributacao-monofasica.calculo.retencao': monoRetencao,
            'regra-tributaria.tributacao-monofasica.calculo.retida-anteriormente': monoRetidaAnteriormente,
            'regra-tributaria.tributacao-monofasica.calculo.diferimento': monoDiferimento,
            'regra-tributaria.credito-presumido.operacao': creditoPresumido,
            'regra-tributaria.credito-presumido.operacao.ibs': creditoPresumido && !!v.creditoPresumido?.ibs?.isAtivo,
            'regra-tributaria.credito-presumido.operacao.cbs': creditoPresumido && !!v.creditoPresumido?.cbs?.isAtivo,
        };

        return this.formulas.filter(formula => contextosCalculados[formula.contextoChave ?? ''] === true);
    }

    private atualizarVariaveisObservacao(): void {
        // O fator do redutor de base está em todo modelo do vBC: a variável só entra com o card "Redução da base de
        // cálculo" ligado.
        const v = this.form.getRawValue();
        const reducaoBaseCalculo = !!v.ibsCbs?.isAtivo && !!v.ibsCbs?.reducaoBaseCalculoAtivo;
        const unicas = listarVariaveisObservacao(this.formulasObservacao())
            .filter(variavel => reducaoBaseCalculo || variavel.chave !== VARIAVEL_REDUCAO_BASE_CALCULO_IBS_CBS);
        const assinaturaAtual = this.variaveisObservacao.map(v => v.chave).join('|');
        if (unicas.map(v => v.chave).join('|') !== assinaturaAtual) {
            this.variaveisObservacao = unicas;
        }
    }

    /** Mesmo fluxo da troca de CST da aba ICMS da natureza, aplicado às observações da regra. */
    private confirmarTrocaClassificacao(): void {
        const nova = this.lerClassificacao();
        if (this.restaurandoClassificacao
            || (nova.cstIbsCbs === this.classificacaoTratada.cstIbsCbs && nova.cclassTrib === this.classificacaoTratada.cclassTrib)) {
            return;
        }
        this.classificacaoTratada = nova;
        this.atualizarVariaveisObservacao();
        // Trocar o CST limpa o cClassTrib incompatível: só confirma quando a nova classificação estiver completa.
        if (!nova.cstIbsCbs || !nova.cclassTrib) return;

        const plano = planejarTrocaCodigoFormulas([], this.formulas, this.formulasObservacao(), {
            observacao: this.form.get('observacao')?.value,
            informacaoFisco: this.form.get('informacaoFisco')?.value,
        });

        if (plano.variaveisPendentes.length === 0) {
            this.aplicarObservacoes(plano.observacoes);
            this.classificacaoAnterior = nova;
            return;
        }

        const pendentes = plano.variaveisPendentes.map(nome => `{${nome}}`).join(', ');
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar troca de classificação tributária',
                description: `A troca para CST ${nova.cstIbsCbs} e cClassTrib ${nova.cclassTrib} deixará pendentes as variáveis [${pendentes}] nas observações. Deseja continuar?`,
                buttonConfirm: 'Continuar',
                buttonClose: 'Cancelar',
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result?.confirm) {
                this.restaurandoClassificacao = true;
                this.classificacaoForm.patchValue(this.classificacaoAnterior);
                this.restaurandoClassificacao = false;
                this.classificacaoTratada = this.classificacaoAnterior;
                this.atualizarVariaveisObservacao();
                return;
            }

            this.aplicarObservacoes(plano.observacoes);
            this.classificacaoAnterior = nova;
        });
    }

    private aplicarObservacoes(observacoes: Record<string, string>): void {
        Object.entries(observacoes).forEach(([campo, valor]) => {
            const control = this.form.get(campo);
            if (control && control.value !== valor) control.setValue(valor);
        });
    }

    public abrirVariaveisDisponiveis(payload: VariaveisCursorPayload): void {
        const editor = payload.editor;
        const data: VariaveisDisponiveisDialogData = {
            variaveis: this.variaveisObservacao,
            targetControl: payload.control,
            cursorStart: payload.selectionStart,
            cursorEnd: payload.selectionEnd,
            onInsert: editor ? (v: VariavelStruct) => editor.inserirVariavel(v) : undefined,
        };

        this.dialog.open(VariaveisDisponiveisDialogComponent, {
            width: '820px',
            maxWidth: '96vw',
            maxHeight: '90vh',
            data,
        });
    }
}
